import { Form, FormRow, ScreenReaderOnly } from '@op/opds.opux';
import { Stepper } from '@op/opds.opux-stepper';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CoverageSelector.module.scss';
import { type Premium } from 'dto/CoveragePremium';
import { CoverageProduct, type Coverage } from 'dto/marketableProduct';
import { useAppStore } from 'state/store';
import { formatEuros, getSuggestedPremium } from 'utils';

import { getSelectedOption } from 'utils/premium-utils';

interface Props {
  coverage: Coverage;
  premium: Premium;
  selectablePremiums: Premium[];
}

const CoverageStepper = ({ coverage, premium, selectablePremiums }: Props) => {
  const [stepperValue, setStepperValue] = useState<number>(premium.sumInsured);
  const { t } = useTranslation(['individualLife']);

  const handleSumInsuredChange = useAppStore(
    (state) => state.handleSumInsuredChange
  );
  const coveragePremiums = useAppStore.use.coveragePremiums();

  const coverageProduct = coverage.coverageProduct;

  useEffect(() => {
    // if the premium is changed then update the stepper value
    if (premium.sumInsured !== stepperValue) {
      setStepperValue(premium.sumInsured);
    }
  }, [premium]);

  useEffect(() => {
    // This is run when stepper value is changed
    // if a premium is found with the new stepper value, then handle selection change
    // otherwise let the user input ay value into the stepper field
    const value = stepperValue.toString();
    if (value) {
      const selectedOption = getSelectedOption(
        value,
        selectablePremiums,
        premium.deductibleDays?.toString()
      );
      // Only call handleSumInsuredChange if the value actually changed
      // This prevents infinite loops when selectablePremiums updates with same values
      if (selectedOption && selectedOption.sumInsured !== premium.sumInsured) {
        handleSumInsuredChange(coverageProduct, selectedOption);
      }
    }
  }, [
    stepperValue,
    premium.sumInsured,
    handleSumInsuredChange,
    coverageProduct,
    selectablePremiums,
  ]);

  const suggestedPremium = coveragePremiums
    ? getSuggestedPremium(coveragePremiums, coverageProduct)
    : undefined;

  const minSumInsuredWithAdditionalCoverages =
    coverage.minSumInsuredWhenOptionalCoverageSelected;

  const isMinSumInsuredWithAdditionalCoveragesSelectable =
    selectablePremiums.find(
      (p) => p.sumInsured === minSumInsuredWithAdditionalCoverages
    );

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
      }}
      stacked
      // @ts-expect-error noValidate does exist on HTMLFormElement
      noValidate>
      <FormRow
        data-testid={`coverageProduct-${coverage.coverageProduct}-stepper-row`}
        labelId={`coverageProduct-${coverage.coverageProduct}-stepper-label`}
        labelFor={`coverageProduct-${coverage.coverageProduct}-stepper-input`}
        label={
          <>
            {coverage.coverageProduct === CoverageProduct.TEMPORARY_DISABILITY
              ? t('coverageProduct.dailyAllowanceCompensation')
              : t('coverageProduct.compensationAmount')}
            <ScreenReaderOnly>
              {t(`coverageProduct.${coverage.coverageProduct}.name`)}
            </ScreenReaderOnly>
          </>
        }
        className={styles.noMargin}>
        <Stepper
          className={styles.coverageStepper}
          inputProps={{
            id: `coverageProduct-${coverage.coverageProduct}-stepper-input`,
            'aria-labelledby': `coverageProduct-${coverage.coverageProduct}-stepper-label`,
            'aria-describedby': `coverageProduct.${coverageProduct}.helperText ${coverageProduct}-minSumInsuredAlert`,
          }}
          data-testid={`coverageProduct-${coverage.coverageProduct}-stepper`}
          value={stepperValue}
          unit={
            coverage.coverageProduct === CoverageProduct.TEMPORARY_DISABILITY
              ? t('common.eurosPerDay')
              : '€'
          }
          onChange={(value) => {
            let valueToSet = value;
            if (value === 0) {
              // When user sets the value to 0:
              // If the coverage contains minSumInsuredWhenOptionalCoverageSelected field and that sum is selectable, set the value to that sum
              // Otherwise set the value to the coverage minSumInsured
              valueToSet =
                minSumInsuredWithAdditionalCoverages &&
                isMinSumInsuredWithAdditionalCoveragesSelectable
                  ? minSumInsuredWithAdditionalCoverages
                  : coverage.minSumInsured;
            }
            setStepperValue(valueToSet);
          }}
          min={
            minSumInsuredWithAdditionalCoverages &&
            isMinSumInsuredWithAdditionalCoveragesSelectable
              ? stepperValue === minSumInsuredWithAdditionalCoverages
                ? minSumInsuredWithAdditionalCoverages
                : 0
              : coverage.minSumInsured
          }
          max={coverage.maxSumInsured}
          step={coverage.sumInsuredStep}
          decreaseButtonProps={{
            'data-testid': `coverageProduct-${coverage.coverageProduct}-stepper-decrease-button`,
          }}
          increaseButtonProps={{
            'data-testid': `coverageProduct-${coverage.coverageProduct}-stepper-increase-button`,
          }}
        />
        <div
          className={styles.helperText}
          id={`coverageProduct.${coverageProduct}.helperText`}>
          {t(`coverageProduct.${coverageProduct}.helperText`, {
            suggestedSumInsured: suggestedPremium?.sumInsured
              ? formatEuros(suggestedPremium.sumInsured)
              : '',
          })}
        </div>
      </FormRow>
    </Form>
  );
};

export default CoverageStepper;
